import { CornerTable } from "../../../../v2/components/CornerTable.jsx";
// src/features/leases/pages/components/PortfolioGantt.jsx
//
// LSE-LANDING-PAGE — SVG-based portfolio Gantt chart. No chart libraries.
//
// Each lease gets one row. Bar left edge = start_date, right edge =
// resolveTimelineEndAnchor (so sentinel-end rolling leases bar to their
// projected anchor). A 2px dashed "Today" line is overlaid.
//
// L52 cite: every end-date read goes through resolveTimelineEndAnchor,
// never lease.endDate directly. L63 cite: all date parsing via parseDateOnly.
//
// LSE-LANDING-PAGE-FOLLOWUP-1:
// - Bar colors route through T tokens and URGENT_RED from leaseTabConfig.
// - termRemainingBucket is mode-aware (Bug 6): projected leases → amber.
// - Today line increased to 2px strokeWidth for visual weight.
// - DaysRemainingPill used for the right-edge badge.
// - "Expired" label uses a small gray pill.
//
// LSE-LEASES-LANDING-TOOLBAR-ALIGN-FOLLOWUP-2:
// C6 — Left column: LABEL_W widened to 200 (fits ~30 chars at 12px);
//      SVG <text> replaced by foreignObject with CSS ellipsis, native
//      tooltip (title attr), hover color (T.blue), and click navigation
//      to the same target as the bar.
// C7 — Sticky header + scrollable body: the Gantt is split into a
//      non-scrolling section (heading, legend, year-axis SVG) and a
//      vertically-scrollable body SVG (rows only). Max-height is
//      viewport-relative so the analytics panels stay above the fold
//      on a 1080p screen.

import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from "react";
import { T } from "../../../../app/tokens.js";
import {
  resolveTimelineEndAnchor,
  parseDateOnly,
  URGENT_RED,
} from "../../leaseTabConfig.js";
import {
  TODAY_LINE_COLOR,
  TODAY_LINE_WIDTH,
  TODAY_LINE_DASH,
} from "../../todayLineStyle.js";
import {
  termRemainingBucket,
  fmtDaysRemaining,
  TERM_REMAINING_BUCKETS,
} from "../../leasePortfolioMetrics.js";
import { DaysRemainingPill } from "../../components/DaysRemainingPill.jsx";
import { LEASES_EMPTY_STATE_MESSAGE, LEASES_TRULY_EMPTY_MESSAGE } from "./LeasePortfolioFilters.jsx";
import { smartSortComparator } from "../../leasePortfolioSort.js";
import { ColorLegendPopup, dotIndicator } from "../../../../components/ColorLegendPopup.jsx";
import { HeadingTooltip } from "../../../../components/HeadingTooltip.jsx";
import { useHoverIntent } from "../../../../components/useHoverIntent.js";
import { ViewScaffold } from "../../../../components/ViewScaffold.jsx";

// C6: widened to 200 to accommodate ~30-char "City · Alias" labels.
const LABEL_W   = 200;
const BADGE_W   = 72;
const ROW_H     = 38;
const BAR_H     = 18;
const HEADER_H     = 24;
const TODAY_BAND_H = 20;
const PAD_YEARS    = 1;


function scaleFor(leases, chartW) {
  if (!leases.length) {
    const now   = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const end   = new Date(now.getFullYear() + 5, 0, 1);
    const totalMs = end - start;
    return {
      start, end,
      xFor: (d) => {
        const dt = parseDateOnly(d) || (d instanceof Date ? d : null);
        if (!dt) return 0;
        return Math.max(0, Math.min(chartW, ((dt - start) / totalMs) * chartW));
      },
      minYear: now.getFullYear(),
      maxYear: now.getFullYear() + 5,
    };
  }

  let minYear = Infinity;
  let maxYear = -Infinity;
  for (const l of leases) {
    const sd = parseDateOnly(l.startDate);
    if (sd) minYear = Math.min(minYear, sd.getFullYear());
    const anchor = resolveTimelineEndAnchor(l);
    if (anchor.date) {
      const ed = parseDateOnly(anchor.date);
      if (ed) maxYear = Math.max(maxYear, ed.getFullYear());
    }
  }
  if (!Number.isFinite(minYear)) minYear = new Date().getFullYear();
  if (!Number.isFinite(maxYear)) maxYear = minYear + 5;
  maxYear += PAD_YEARS;

  const start   = new Date(minYear, 0, 1);
  const end     = new Date(maxYear + 1, 0, 1);
  const totalMs = end - start;

  return {
    start, end, minYear, maxYear,
    xFor: (d) => {
      const dt = parseDateOnly(d) || (d instanceof Date ? d : null);
      if (!dt) return 0;
      return Math.max(0, Math.min(chartW, ((dt - start) / totalMs) * chartW));
    },
  };
}

// LSE-GANTT-YEAR-MARKERS-COMPACT-FORMAT: every year is labelled regardless of
// range. Compact apostrophe format ('18, '19, …) is ~25% narrower than 4-digit
// labels, making every-year density visually workable.
// Gridlines are still drawn every year.
function yearTicks(minYear, maxYear, chartW, scale) {
  const ticks = [];
  for (let y = minYear; y <= maxYear + 1; y++) {
    const x = scale.xFor(new Date(y, 0, 1));
    ticks.push({ year: y, x, showLabel: true });
  }
  return ticks;
}

// C6: LabelCell renders as foreignObject content — HTML div with ellipsis
// CSS and hover color. Click navigates to the same target as the bar.
function LabelCell({ city, alias, onClick }) {
  const [hovered, setHovered] = useState(false);
  const fullName = alias ? `${city} · ${alias}` : city;
  return (
    <div
      onClick={onClick}
      title={fullName}
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        padding: "0 8px",
        overflow: "hidden",
        cursor: "pointer",
        boxSizing: "border-box",
      }}
    >
      <span
        style={{
          fontSize: T.fsSm,
          color: hovered ? T.blue : T.dark,
          display: "block",
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          userSelect: "none",
          transition: "color 0.1s",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span style={{ fontWeight: T.fwSemibold }}>{city}</span>
        {alias && (
          <span style={{ fontWeight: T.fwRegular, color: T.mid }}>{" · "}{alias}</span>
        )}
      </span>
    </div>
  );
}

function LeaseRow({ lease, scale, chartW, rowY, today, onSelectLocation }) {
  const startDate = parseDateOnly(lease.startDate);
  const anchor    = resolveTimelineEndAnchor(lease);
  const endDate   = anchor.date ? parseDateOnly(anchor.date) : null;

  if (!startDate) return null;

  const x1   = scale.xFor(startDate);
  const x2   = endDate ? scale.xFor(endDate) : chartW;
  const barW = Math.max(x2 - x1, 4);

  const bucket   = termRemainingBucket(lease, today);
  // Rolling leases use the SVG pattern defined in <defs>; all others use solid color.
  const barFill  = bucket.stripe ? "url(#rolling-stripes)" : bucket.color;
  const barY      = rowY + (ROW_H - BAR_H) / 2;

  const daysLeft   = fmtDaysRemaining(lease, today);
  const cityAlias = [lease.city, lease.locationAlias].filter(Boolean).join(" · ")
    || String(lease.locationId || "");

  const tooltipLines = [
    cityAlias,
    lease.leaseType || lease.type || "",
    startDate
      ? `${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} – ${
          endDate
            ? endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            : "ongoing"
        }`
      : "",
    daysLeft != null ? `${daysLeft} days remaining` : "",
  ].filter(Boolean);

  return (
    <g
      style={{ cursor: "pointer" }}
      onClick={() => onSelectLocation && onSelectLocation(lease.locationId, "lease")}
    >
      <rect
        x={LABEL_W + x1}
        y={barY}
        width={barW}
        height={BAR_H}
        rx={4}
        fill={barFill}
      />
      <title>{tooltipLines.filter(Boolean).join(" · ")}</title>
    </g>
  );
}

// How long (ms) a just-confirmed row stays pinned to the bottom before it
// moves to its real sorted position. Long enough to feel intentional, short
// enough that the list doesn't feel stale. See task-363.
const JUST_CONFIRMED_HOLD_MS = 800;

// justConfirmedIds is an optional Set<string> of real IDs that were recently
// promoted from a temp-id. They are treated like temp rows in the tie-breaker
// so the row holds its bottom position for one brief window before settling.
export function listSortComparator(a, b, sortKey, sortDir, today, justConfirmedIds = null) {
  let av, bv;
  if (sortKey === "id") {
    // Temp ids always pin to the bottom regardless of sortDir (same rule as
    // the tie-breaker below). Real ids are compared with sortDir applied.
    const aT = typeof a.id === "string" && a.id.startsWith("temp-");
    const bT = typeof b.id === "string" && b.id.startsWith("temp-");
    if (aT && !bT) return 1;
    if (!aT && bT) return -1;
    av = String(a.id || ""); bv = String(b.id || "");
  }
  else if (sortKey === "city")        { av = a.city || ""; bv = b.city || ""; }
  else if (sortKey === "type")   { av = a.leaseType || ""; bv = b.leaseType || ""; }
  else if (sortKey === "start")  { av = a.startDate || ""; bv = b.startDate || ""; }
  else if (sortKey === "end") {
    const aa = resolveTimelineEndAnchor(a);
    const bb = resolveTimelineEndAnchor(b);
    av = aa.date || "9999"; bv = bb.date || "9999";
  }
  else if (sortKey === "days") {
    av = fmtDaysRemaining(a, today) ?? 99999;
    bv = fmtDaysRemaining(b, today) ?? 99999;
  }
  else if (sortKey === "cost")   { av = Number(a.totalCost) || 0; bv = Number(b.totalCost) || 0; }
  else if (sortKey === "status") { av = a.status || ""; bv = b.status || ""; }
  if (av < bv) return -sortDir;
  if (av > bv) return sortDir;
  // Secondary tie-breaker: stable order by id so tied leases never shuffle.
  // Optimistic rows (temporary ids like "temp-1234567890") are pinned to the
  // bottom so they never flash to a different position before the server
  // confirms the real id. "Just-confirmed" rows (temp id recently replaced by
  // a real id) are also pinned for JUST_CONFIRMED_HOLD_MS so the row holds
  // position instead of jumping immediately. (task-363)
  const aTemp = (typeof a.id === "string" && a.id.startsWith("temp-")) ||
    (justConfirmedIds != null && justConfirmedIds.has(String(a.id)));
  const bTemp = (typeof b.id === "string" && b.id.startsWith("temp-")) ||
    (justConfirmedIds != null && justConfirmedIds.has(String(b.id)));
  if (aTemp && !bTemp) return 1;
  if (!aTemp && bTemp) return -1;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
}

// Keyboard-accessible sort headers need a :focus-visible ring that inline
// styles cannot express. A scoped <style> block (same pattern as
// CommentaryPanel's hover-reveal CSS) is injected once by ListTable.
// Class name is intentionally specific to avoid collisions.
const GANTT_TH_CSS = `
.gantt-sort-th:focus-visible {
  outline: 2px solid ${T.blue};
  outline-offset: -2px;
}
@keyframes gantt-row-fadein {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.gantt-row-confirmed {
  animation: gantt-row-fadein 0.35s ease-out;
}
`;

// Tooltip container style — matches LeasesListTable TH_TIP_STYLE spec:
// 0.5px border, 8px radius, lighter shadow, 13px/16px padding.
// textTransform:"none" overrides the uppercase inherited from parent headers.
const DAYS_TIP_STYLE = {
  background:    T.white,
  color:         T.dark,
  border:        `0.5px solid ${T.border}`,
  borderRadius:  8,
  boxShadow:     T.shadowTableHdr,
  padding:       "13px 16px",
  zIndex:        20,
  pointerEvents: "none",
  textTransform: "none",
};

// Color-legend tooltip for "Days Remaining" / "Days Left" column headers.
// Built from TERM_REMAINING_BUCKETS so it stays in sync with bar colors.
const DAYS_REMAINING_TOOLTIP = (
  <ColorLegendPopup rows={[
    { indicator: dotIndicator(TERM_REMAINING_BUCKETS.ACTIVE_3YR_PLUS.color, "green"), label: TERM_REMAINING_BUCKETS.ACTIVE_3YR_PLUS.label },
    { indicator: dotIndicator(TERM_REMAINING_BUCKETS.UPCOMING_1_TO_3.color, "amber"), label: TERM_REMAINING_BUCKETS.UPCOMING_1_TO_3.label },
    { indicator: dotIndicator(TERM_REMAINING_BUCKETS.URGENT_UNDER_1.color,  "red"),   label: TERM_REMAINING_BUCKETS.URGENT_UNDER_1.label  },
    { indicator: dotIndicator(TERM_REMAINING_BUCKETS.EXPIRED.color,         "gray"),  label: TERM_REMAINING_BUCKETS.EXPIRED.label         },
  ]} />
);


// tooltip prop — optional JSX content for a column-heading popup.
// When supplied the <th> gains: aria-describedby, onFocus/onBlur (open/close),
// and Escape-key dismiss — same WCAG 2.1 AA pattern as Task-430 (LeasesListTable).
function Th({ col, label, sortKey, sortDir, onToggle, tooltip }) {
  const isActive = sortKey === col;
  const thRef    = useRef(null);
  const intent = useHoverIntent({ enabled: Boolean(tooltip) });
  const tipOpen = intent.open;
  return (
    <th
      ref={thRef}
      className="gantt-sort-th"
      data-testid={`gantt-list-th-${col}`}
      aria-sort={isActive ? (sortDir > 0 ? "ascending" : "descending") : "none"}
      aria-describedby={tipOpen ? `th-tooltip-${col}` : undefined}
      tabIndex={0}
      {...intent.triggerProps}
      onClick={() => { intent.dismiss(); onToggle(col); }}
      onKeyDown={(e) => {
        intent.triggerProps.onKeyDown(e);
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); intent.dismiss(); onToggle(col); }
      }}
      style={{
        padding: "8px 12px", textAlign: "left", cursor: "pointer",
        fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: "0.06em",
        textTransform: "uppercase", color: T.mid, userSelect: "none",
        borderBottom: `1px solid ${T.border}`,
        background: T.lightGray,
        whiteSpace: "nowrap",
      }}
    >
      {label}
      <span
        aria-hidden="true"
        data-testid={`gantt-list-th-arrow-${col}`}
        style={{ marginLeft: 4 }}
      >
        {isActive ? (sortDir > 0 ? "↑" : "↓") : "↕"}
      </span>
      {tooltip && tipOpen && (
        <HeadingTooltip
          triggerRef={thRef}
          id={`th-tooltip-${col}`}
          role="tooltip"
          data-testid={`th-tooltip-${col}`}
          style={DAYS_TIP_STYLE}
        >
          {tooltip}
        </HeadingTooltip>
      )}
    </th>
  );
}

function ListTable({ leases, today, onSelectLocation, isTrulyEmpty }) {
  const [sortKey, setSortKey] = useState("city");
  const [sortDir, setSortDir] = useState(1);

  // task-363: track which real IDs were just promoted from a temp id so the
  // comparator can keep them pinned to the bottom for JUST_CONFIRMED_HOLD_MS
  // instead of snapping them to their sorted position immediately.
  const [justConfirmedIds, setJustConfirmedIds] = useState(() => new Set());
  const prevLeaseIdsRef = useRef(new Set());

  // task-533: FLIP animation refs.
  // rowRefsRef holds a DOM ref for each rendered <tr> keyed by lease id.
  // prePositionsRef stores the measured top-offsets of confirmed rows
  // captured just before justConfirmedIds is cleared, so useLayoutEffect
  // can compute the delta and animate the slide to the final position.
  const rowRefsRef    = useRef({});
  const prePositionsRef = useRef(null);

  useEffect(() => {
    const currentIds = new Set(leases.map((l) => String(l.id)));
    const prevIds = prevLeaseIdsRef.current;

    const lostTempIds = [...prevIds].filter((id) => id.startsWith("temp-") && !currentIds.has(id));
    const gainedRealIds = [...currentIds].filter((id) => !id.startsWith("temp-") && !prevIds.has(id));

    prevLeaseIdsRef.current = currentIds;

    if (lostTempIds.length > 0 && gainedRealIds.length > 0) {
      const confirmed = new Set(gainedRealIds);
      setJustConfirmedIds(confirmed);
      const t = setTimeout(() => {
        // FLIP "First": snapshot confirmed rows' vertical positions before
        // the sort re-runs so useLayoutEffect can compute the travel distance.
        const snapshots = {};
        for (const id of confirmed) {
          const el = rowRefsRef.current[id];
          if (el) snapshots[id] = el.getBoundingClientRect().top;
        }
        prePositionsRef.current = snapshots;
        setJustConfirmedIds(new Set());
      }, JUST_CONFIRMED_HOLD_MS);
      return () => clearTimeout(t);
    }
  }, [leases]);

  // FLIP "Last → Invert → Play": after React commits the re-sorted DOM,
  // measure each confirmed row's new position, apply an inverted transform
  // to make it appear to start at the old position, then release the
  // transform via CSS transition so it slides smoothly to its real spot.
  useLayoutEffect(() => {
    if (!prePositionsRef.current) return;
    const snapshots = prePositionsRef.current;
    prePositionsRef.current = null;
    for (const [id, fromTop] of Object.entries(snapshots)) {
      const el = rowRefsRef.current[id];
      if (!el) continue;
      const dy = fromTop - el.getBoundingClientRect().top;
      if (Math.abs(dy) < 1) continue;
      el.style.transition = "none";
      el.style.transform  = `translateY(${dy}px)`;
      el.getBoundingClientRect(); // force reflow before releasing
      el.style.transition = "transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
      el.style.transform  = "";
      const onEnd = () => {
        el.style.transition = "";
        el.removeEventListener("transitionend", onEnd);
      };
      el.addEventListener("transitionend", onEnd);
    }
  }, [justConfirmedIds]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => -d);
    else { setSortKey(key); setSortDir(1); }
  };

  const sorted = useMemo(
    () => [...leases].sort((a, b) => listSortComparator(a, b, sortKey, sortDir, today, justConfirmedIds)),
    [leases, sortKey, sortDir, today, justConfirmedIds],
  );

  const typeLabel = {
    direct_lease: "Direct", serviced_office: "Serviced Office",
    sublease_inbound: "Sublease (In)", sublease_outbound: "Sublease (Out)",
  };

  const statusPill = (status) => {
    const active = status === "active";
    return (
      <span
        style={{
          fontSize: T.fsXs, fontWeight: T.fwSemibold, padding: "2px 7px", borderRadius: 10,
          background: active ? T.utilGreen     : T.lightGray,
          color:      active ? T.utilGreenText : T.mid,
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <style>{GANTT_TH_CSS}</style>
      <CornerTable style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsBase }}>
        <thead>
          <tr>
            <Th col="city"   label="City"           sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
            <Th col="type"   label="Lease Type"     sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
            <Th col="start"  label="Start"          sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
            <Th col="end"    label="End"            sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
            <Th col="days"   label="Days Remaining" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} tooltip={DAYS_REMAINING_TOOLTIP} />
            <Th col="cost"   label="Annual Cost"    sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
            <Th col="status" label="Status"         sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td
                colSpan={7}
                data-testid="gantt-list-empty"
                style={{ padding: "24px 12px", textAlign: "center", color: T.mid }}
              >
                {isTrulyEmpty ? LEASES_TRULY_EMPTY_MESSAGE : LEASES_EMPTY_STATE_MESSAGE}
              </td>
            </tr>
          )}
          {sorted.map((l) => {
            const anchor = resolveTimelineEndAnchor(l);
            const endD   = anchor.date ? parseDateOnly(anchor.date) : null;
            const days   = fmtDaysRemaining(l, today);
            return (
              <tr
                key={l.id}
                ref={(el) => {
                  if (el) rowRefsRef.current[l.id] = el;
                  else delete rowRefsRef.current[l.id];
                }}
                className={justConfirmedIds.has(String(l.id)) ? "gantt-row-confirmed" : undefined}
                style={{ cursor: "pointer", borderBottom: `1px solid ${T.border}` }}
                onClick={() => onSelectLocation && onSelectLocation(l.locationId, "lease")}
                onMouseEnter={(e) => (e.currentTarget.style.background = T.hoverBg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}
              >
                <td style={{ padding: "10px 12px", fontWeight: T.fwSemibold, color: T.dark }}>
                  {[l.city, l.locationAlias].filter(Boolean).join(" · ") || l.locationId || "—"}
                </td>
                <td style={{ padding: "10px 12px", color: T.mid }}>
                  {typeLabel[l.leaseType] || l.leaseType || "—"}
                </td>
                <td style={{ padding: "10px 12px", color: T.mid }}>
                  {parseDateOnly(l.startDate)?.toLocaleDateString("en-US", {
                    month: "short", day: "numeric", year: "numeric",
                  }) || "—"}
                </td>
                <td style={{ padding: "10px 12px", color: T.mid }}>
                  {endD ? endD.toLocaleDateString("en-US", {
                    month: "short", day: "numeric", year: "numeric",
                  }) : "Ongoing"}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  {days != null ? <DaysRemainingPill days={days} /> : <span style={{ color: T.light }}>—</span>}
                </td>
                <td style={{ padding: "10px 12px", color: T.mid }}>
                  {l.totalCost != null
                    ? `${l.currency} ${Number(l.totalCost).toLocaleString()}`
                    : "—"}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  {statusPill(l.status)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </CornerTable>
    </div>
  );
}

// CONTEXT-BANDS-S1: Legend items fed to the shared ViewScaffold (replaces
// the bespoke inline BUCKET_LEGEND chips). BUCKET_LEGEND is kept for
// backwards compat — GANTT_LEGEND_ITEMS derives from it instead.
// Rolling's green+amber stripe is passed as a gradient color string so
// ViewScaffold's LegendSwatch renders it identically to the chart bars.
const GANTT_LEGEND_ITEMS = [
  { key: "active-3yr", label: TERM_REMAINING_BUCKETS.ACTIVE_3YR_PLUS.label, indicator: { color: TERM_REMAINING_BUCKETS.ACTIVE_3YR_PLUS.color, shape: "square" } },
  { key: "upcoming",   label: TERM_REMAINING_BUCKETS.UPCOMING_1_TO_3.label, indicator: { color: TERM_REMAINING_BUCKETS.UPCOMING_1_TO_3.color, shape: "square" } },
  { key: "urgent",     label: TERM_REMAINING_BUCKETS.URGENT_UNDER_1.label,  indicator: { color: TERM_REMAINING_BUCKETS.URGENT_UNDER_1.color,  shape: "square" } },
  { key: "rolling",    label: TERM_REMAINING_BUCKETS.ROLLING.label,         indicator: { color: `repeating-linear-gradient(135deg, ${T.green} 0px, ${T.green} 4px, ${T.amber} 4px, ${T.amber} 8px)`, shape: "square" } },
  { key: "expired",    label: TERM_REMAINING_BUCKETS.EXPIRED.label,         indicator: { color: TERM_REMAINING_BUCKETS.EXPIRED.color,         shape: "square" } },
  { key: "today",      label: "Today",                                       indicator: { color: TODAY_LINE_COLOR, type: "line" } },
];

export function PortfolioGantt({ leases = [], totalLeaseCount, today = new Date(), onSelectLocation, listView = false, sortKey = "smart" }) {
  // isTrulyEmpty: the raw portfolio has no leases at all (not just filtered to zero).
  const isTrulyEmpty = (totalLeaseCount ?? leases.length) === 0;

  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(800);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerW(Math.floor(entry.contentRect.width));
    });
    ro.observe(containerRef.current);
    setContainerW(containerRef.current.offsetWidth);
    return () => ro.disconnect();
  }, []);

  // Timeline row order is driven by the dropdown sort in LeasesPage.
  // When sortKey === "smart" (the default), apply smartSortComparator directly —
  // this also covers tests that render PortfolioGantt without a sortKey prop.
  // For every other dropdown key the parent has already sorted filteredLeases
  // via applyLeaseSort, so we preserve that order as-is.
  const sortedLeases = useMemo(
    () => sortKey === "smart"
      ? [...leases].sort(smartSortComparator)
      : [...leases],
    [leases, sortKey],
  );

  if (listView) {
    return (
      <div ref={containerRef} data-testid="portfolio-list">
        <ListTable leases={leases} today={today} onSelectLocation={onSelectLocation} isTrulyEmpty={isTrulyEmpty} />
      </div>
    );
  }

  const chartW  = Math.max(containerW - LABEL_W - BADGE_W, 200);
  const scale   = scaleFor(sortedLeases, chartW);
  const totalW  = LABEL_W + chartW + BADGE_W;
  const ticks   = yearTicks(scale.minYear, scale.maxYear, chartW, scale);
  const todayX  = LABEL_W + scale.xFor(today);
  const rowsH   = ROW_H * (sortedLeases.length || 1);

  const minYearLabel = scale.minYear ?? new Date().getFullYear();
  const maxYearLabel = scale.maxYear ?? minYearLabel + 5;

  return (
    // C7: outer div is a width-reference; no overflow here so sticky
    // is unrestricted. Horizontal scroll sits on the inner SVGs via
    // overflowX on the header and body wrappers.
    <div ref={containerRef} data-testid="portfolio-gantt">

      {/* ── Non-scrolling header: heading + legend + year-axis ──────── */}
      {/* This section stays visible as the row body scrolls.           */}
      <div
        style={{ background: T.white, paddingBottom: 2 }}
        data-testid="gantt-header"
      >
        {/* Sub-heading row */}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: "0.07em",
              textTransform: "uppercase", color: T.mid,
            }}
          >
            Lease Portfolio
          </span>
          <span style={{ fontSize: T.fsXs, fontWeight: T.fwRegular, color: T.light }}>
            {minYearLabel}-{maxYearLabel}
          </span>
        </div>

        {/* Legend row — CONTEXT-BANDS-S1: shared ViewScaffold */}
        <div style={{ marginBottom: 6 }}>
          <ViewScaffold legendItems={GANTT_LEGEND_ITEMS} />
        </div>

        {/* Year-axis SVG — Today band (top) + year labels. */}
        <div style={{ overflowX: "auto" }}>
          <svg
            width={totalW}
            height={TODAY_BAND_H + HEADER_H}
            style={{ display: "block" }}
            aria-hidden="true"
          >
            {/* Today band — dedicated row above year markers */}
            {(() => {
              const PILL_W = 34;
              const pillX = Math.min(
                Math.max(todayX - PILL_W / 2, LABEL_W),
                LABEL_W + chartW - PILL_W,
              );
              const pillY = (TODAY_BAND_H - 14) / 2;
              return (
                <g data-testid="gantt-today-band">
                  <rect
                    x={pillX}
                    y={pillY}
                    width={PILL_W}
                    height={14}
                    rx={3}
                    fill="rgba(255,255,255,0.85)"
                  />
                  <text
                    x={pillX + PILL_W / 2}
                    y={pillY + 11}
                    fontSize={T.fsXxs}
                    fill={TODAY_LINE_COLOR}
                    fontWeight={500}
                    textAnchor="middle"
                  >
                    TODAY
                  </text>
                </g>
              );
            })()}

            {/* Year labels shifted down by TODAY_BAND_H, centered per column width */}
            {ticks.map((tick, i) => {
              if (!tick.showLabel) return null;
              const { year, x } = tick;
              const nextX = i + 1 < ticks.length ? ticks[i + 1].x : chartW;
              const colW  = nextX - x;
              const labelX = i + 1 < ticks.length
                ? LABEL_W + x + colW / 2
                : LABEL_W + x + 3;
              return (
                <text
                  key={year}
                  x={labelX}
                  y={TODAY_BAND_H + HEADER_H - 6}
                  fontSize={T.fsXs}
                  fill={T.mid}
                  textAnchor="middle"
                >
                  {`'${String(year).slice(-2)}`}
                </text>
              );
            })}
          </svg>
        </div>
      </div>

      {/* ── Row body ─────────────────────────────────────────────────── */}
      {/* LSE-LEASES-LANDING-TIMELINE-CLEANUP (C3): maxHeight clamp and  */}
      {/* overflowY removed — Gantt runs at natural length; page scrolls. */}
      {sortedLeases.length === 0 ? (
        <div
          data-testid="gantt-chart-empty"
          style={{
            height: 160,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: T.mid,
            fontSize: T.fsMd,
            border: `1px dashed ${T.border}`,
            borderRadius: 8,
            marginTop: 4,
          }}
        >
          {isTrulyEmpty ? LEASES_TRULY_EMPTY_MESSAGE : LEASES_EMPTY_STATE_MESSAGE}
        </div>
      ) : (
        <div
          data-testid="gantt-body"
          style={{
            overflowX: "auto",
          }}
        >
          <svg
            width={totalW}
            height={rowsH + 20}
            style={{ display: "block" }}
            data-testid="gantt-svg"
          >
            {/* Rolling-lease stripe pattern. Used by LeaseRow when bucket.stripe===true. */}
            <defs>
              <pattern
                id="rolling-stripes"
                patternUnits="userSpaceOnUse"
                width="16"
                height="16"
                patternTransform="rotate(135)"
              >
                <rect width="8" height="16" fill={T.green} />
                <rect x="8" width="8" height="16" fill={T.amber} />
              </pattern>
            </defs>

            {/* Year gridlines (vertical, spanning all rows) */}
            {ticks.map(({ year, x }) => (
              <line
                key={year}
                x1={LABEL_W + x} y1={0}
                x2={LABEL_W + x} y2={rowsH}
                stroke={T.border}
                strokeWidth={1}
              />
            ))}

            {/* Pill-column separator — visual boundary between chart and pill column */}
            <line
              x1={LABEL_W + chartW} y1={0}
              x2={LABEL_W + chartW} y2={rowsH}
              stroke={T.border}
              strokeWidth={1}
              data-testid="gantt-pill-separator"
            />

            {/* Today line — label is in the header Today band above */}
            <line
              x1={todayX} y1={0}
              x2={todayX} y2={rowsH}
              stroke={TODAY_LINE_COLOR}
              strokeWidth={TODAY_LINE_WIDTH}
              strokeDasharray={TODAY_LINE_DASH}
            />

            {/* Lease rows */}
            {sortedLeases.map((lease, i) => {
              const rowY = ROW_H * i;
              const days = fmtDaysRemaining(lease, today);
              const city  = lease.city || "";
              const alias = lease.locationAlias || "";
              const fullName = alias
                ? `${city} · ${alias}`
                : city || String(lease.locationId || "");
              const handleClick = () =>
                onSelectLocation && onSelectLocation(lease.locationId, "lease");

              return (
                <g key={lease.id}>
                  {/* Alternating stripe */}
                  <rect
                    x={0} y={rowY}
                    width={totalW} height={ROW_H}
                    fill={i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.02)"}
                  />

                  {/* C6: foreignObject label — ellipsis, title tooltip, hover color, click nav.
                      onClick on the foreignObject element itself handles JSDOM (tests);
                      LabelCell's inner onClick handles real browser interaction. */}
                  <foreignObject
                    x={0} y={rowY}
                    width={LABEL_W} height={ROW_H}
                    data-testid={`gantt-label-${lease.locationId}`}
                    onClick={handleClick}
                    style={{ cursor: "pointer" }}
                  >
                    <LabelCell city={city || String(lease.locationId || "")} alias={alias} onClick={handleClick} />
                  </foreignObject>

                  {/* Bar */}
                  <LeaseRow
                    lease={lease}
                    scale={scale}
                    chartW={chartW}
                    rowY={rowY}
                    today={today}
                    onSelectLocation={onSelectLocation}
                  />

                  {/* Right-edge badge */}
                  {lease.status === "expired" ? (
                    <foreignObject
                      x={LABEL_W + chartW + 4}
                      y={rowY + (ROW_H - 18) / 2}
                      width={BADGE_W - 4}
                      height={18}
                      style={{ overflow: "visible", pointerEvents: "none" }}
                    >
                      <span
                        style={{
                          fontSize: T.fsXxs,
                          fontWeight: T.fwSemibold,
                          padding: "2px 6px",
                          borderRadius: 10,
                          background: T.lightGray,
                          color: T.mid,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Expired
                      </span>
                    </foreignObject>
                  ) : days != null ? (
                    <foreignObject
                      x={LABEL_W + chartW + 4}
                      y={rowY + (ROW_H - 18) / 2}
                      width={BADGE_W - 4}
                      height={18}
                      style={{ overflow: "visible", pointerEvents: "none" }}
                    >
                      <DaysRemainingPill days={days} />
                    </foreignObject>
                  ) : null}
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}
